import React from "react";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Zap,
  TrendingUp,
  Info,
} from "lucide-react";

// --- MOCK DATA: COACHING SIGNALS ---
const WEEKLY_REPORT = {
  week: 3,
  status: "OPTIMIZING",
  context: "Coach interpretation based on your last check-in.",
  summary:
    "Your system is improving. Recovery is the current limiter — not effort.",

  // METRICS: Qualitative states
  metrics: [
    {
      label: "Movement Quality",
      status: "Strong",
      level: 9,
      color: "var(--brand)",
    },
    {
      label: "Recovery Capacity",
      status: "Limited",
      level: 4,
      color: "#f59e0b",
    }, // Amber
    { label: "Consistency", status: "High", level: 9, color: "var(--brand)" },
    { label: "Stress Load", status: "Elevated", level: 6, color: "#3b82f6" }, // Blue
  ],

  // LOGIC: The "Why"
  constraint: "Sleep Variability",
  adjustment: "Reduce training volume slightly to match recovery capacity.",

  // WINS: Cause -> Effect
  wins: [
    {
      cause: "Hip mobility improved",
      effect: "Squat depth increased w/o pain",
    },
    { cause: "Sleep stabilized (3 days)", effect: "Morning energy stable" },
    { cause: "Training consistency", effect: "Self-trust confidence up" },
  ],
};

// --- COMPONENT: Qualitative Bar ---
const SystemBar = ({ label, status, level, color }) => {
  const blocks = Array.from({ length: 10 }, (_, i) => i + 1);
  return (
    <div style={{ marginBottom: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "8px",
          fontSize: "12px",
          fontWeight: 700,
          letterSpacing: "1px",
          textTransform: "uppercase",
        }}
      >
        <span style={{ color: "var(--muted)" }}>{label}</span>
        <span style={{ color: color }}>{status}</span>
      </div>
      {/* TWEAK #2b: Slightly softened opacity on bars to let the Right Panel shine */}
      <div style={{ display: "flex", gap: "4px", height: "8px", opacity: 0.9 }}>
        {blocks.map((b) => (
          <div
            key={b}
            style={{
              flex: 1,
              background: level >= b ? color : "rgba(255,255,255,0.05)", // Lowered contrast on empty blocks
              opacity: level >= b ? 1 : 0.3,
              borderRadius: "2px",
              transition: "all 0.3s ease",
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default function ResultsView() {
  const data = WEEKLY_REPORT;

  return (
    <div className="container" style={{ paddingBottom: 80 }}>
      {/* HEADER */}
      <div className="secHead" style={{ alignItems: "flex-start" }}>
        <div>
          <h2>System Signals</h2>
          <div
            style={{
              color: "var(--brand)",
              fontWeight: 700,
              marginBottom: "4px",
            }}
          >
            Week {data.week} Report
          </div>
          <div
            className="muted"
            style={{
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Info size={14} /> {data.context}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "rgba(34,197,94,0.1)",
            padding: "8px 16px",
            borderRadius: "20px",
            border: "1px solid rgba(34,197,94,0.2)",
          }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              background: "var(--brand)",
              borderRadius: "50%",
              boxShadow: "0 0 10px var(--brand)",
            }}
          />
          <span
            style={{
              fontSize: "11px",
              fontWeight: 800,
              color: "var(--brand)",
              letterSpacing: "1px",
            }}
          >
            {data.status}
          </span>
        </div>
      </div>

      {/* DASHBOARD GRID */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "24px",
          marginBottom: "40px",
        }}
      >
        {/* LEFT: SYSTEM SNAPSHOT (Renamed from Current State) */}
        {/* TWEAK #2b: Slightly reduced contrast on container to push visual hierarchy to the right */}
        <div
          className="glass"
          style={{
            padding: "32px",
            background: "rgba(15, 23, 42, 0.4)",
            borderColor: "rgba(255,255,255,0.05)",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              marginBottom: "24px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {/* TWEAK #1: Renamed Header */}
            <Activity size={18} color="var(--brand)" /> System Snapshot
          </h3>

          {data.metrics.map((m, i) => (
            <SystemBar
              key={i}
              label={m.label}
              status={m.status}
              level={m.level}
              color={m.color}
            />
          ))}

          <div
            style={{
              marginTop: "32px",
              padding: "16px",
              background: "rgba(255,255,255,0.03)",
              borderRadius: "12px",
              fontSize: "14px",
              lineHeight: "1.5",
              color: "var(--muted)",
              fontStyle: "italic",
            }}
          >
            "{data.summary}"
          </div>
        </div>

        {/* RIGHT: COACH'S LOGIC (Visually Dominant) */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* THE CONSTRAINT */}
          <div
            className="glass"
            style={{
              padding: "28px",
              // TWEAK #2a: Stronger Left Border + Subtle Background Glow to grab attention
              borderLeft: "6px solid #f59e0b",
              background:
                "linear-gradient(90deg, rgba(245, 158, 11, 0.08) 0%, rgba(15, 23, 42, 0.6) 100%)",
              boxShadow: "0 10px 30px -10px rgba(0,0,0,0.5)",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                fontWeight: 800,
                color: "#f59e0b",
                textTransform: "uppercase",
                letterSpacing: "1px",
                marginBottom: "12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <AlertTriangle size={14} /> Primary Focus
            </div>
            <div
              style={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#fff",
                marginBottom: "12px",
                letterSpacing: "-0.5px",
              }}
            >
              {data.constraint}
            </div>
            <p
              style={{
                margin: 0,
                fontSize: "15px",
                color: "var(--muted)",
                lineHeight: "1.5",
              }}
            >
              This is what matters most right now. Improving this unlocks the
              rest of the system.
            </p>
          </div>

          {/* THE ADJUSTMENT */}
          <div
            className="glass highlight"
            style={{
              padding: "28px",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                fontWeight: 800,
                color: "var(--brand)",
                textTransform: "uppercase",
                letterSpacing: "1px",
                marginBottom: "12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Zap size={14} /> Recommended Adjustment
            </div>
            <div
              style={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#fff",
                marginBottom: "20px",
                lineHeight: "1.4",
              }}
            >
              "{data.adjustment}"
            </div>

            <div style={{ marginTop: "auto" }}>
              <button
                className="btn primary"
                style={{ width: "100%", justifyContent: "center" }}
              >
                Accept Adjustment <ArrowRight size={16} />
              </button>

              {/* TWEAK #3: The Reassurance Micro-line */}
              <div
                style={{
                  textAlign: "center",
                  marginTop: "12px",
                  fontSize: "11px",
                  color: "var(--muted)",
                  opacity: 0.8,
                }}
              >
                This keeps progress moving without burnout.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. WINS SECTION */}
      <div className="glass" style={{ padding: "32px" }}>
        <div
          style={{
            fontSize: "12px",
            fontWeight: 800,
            color: "var(--muted)",
            textTransform: "uppercase",
            letterSpacing: "1px",
            marginBottom: "24px",
          }}
        >
          Since Last Check-in
        </div>

        <div style={{ display: "grid", gap: "16px" }}>
          {data.wins.map((win, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                paddingBottom: "16px",
                borderBottom:
                  i !== data.wins.length - 1 ? "1px solid var(--line)" : "none",
              }}
            >
              <div
                style={{
                  background: "rgba(34,197,94,0.1)",
                  borderRadius: "50%",
                  padding: "4px",
                }}
              >
                <CheckCircle2 size={20} color="var(--brand)" />
              </div>
              <div style={{ fontSize: "15px" }}>
                <span style={{ color: "#fff", fontWeight: 600 }}>
                  {win.cause}
                </span>
                <span style={{ margin: "0 12px", color: "var(--muted)" }}>
                  →
                </span>
                <span style={{ color: "var(--brand)" }}>{win.effect}</span>
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: "24px",
            paddingTop: "24px",
            borderTop: "1px solid var(--line)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              color: "var(--muted)",
              fontSize: "14px",
              fontStyle: "italic",
            }}
          >
            <TrendingUp size={16} /> "Progress isn’t intensity. It’s alignment."
          </div>
        </div>
      </div>
    </div>
  );
}
